<script setup>
import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const accountId = computed(() => store.getters.getCurrentAccountId);
const account = computed(
  () => store.getters['accounts/getAccount'](Number(accountId.value)) || {}
);
const subscription = computed(() => account.value.vincto_subscription || {});
const isPastDue = computed(() => subscription.value.status === 'past_due');
const isTrialExpired = computed(() => subscription.value.status === 'expired');
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);
const globalConfig = computed(() => store.getters['globalConfig/get']);

const title = computed(() => {
  if (isTrialExpired.value) {
    return t('APP_GLOBAL.ACCOUNT_SUSPENDED.TRIAL_ENDED_TITLE');
  }
  if (isPastDue.value) {
    return t('APP_GLOBAL.ACCOUNT_SUSPENDED.PAST_DUE_TITLE');
  }
  return t('APP_GLOBAL.ACCOUNT_SUSPENDED.TITLE');
});

const message = computed(() => {
  if (isTrialExpired.value) {
    return isAdmin.value
      ? t('APP_GLOBAL.ACCOUNT_SUSPENDED.TRIAL_ENDED_MESSAGE')
      : t('APP_GLOBAL.ACCOUNT_SUSPENDED.TRIAL_ENDED_AGENT_MESSAGE');
  }
  if (isPastDue.value) {
    return t('APP_GLOBAL.ACCOUNT_SUSPENDED.PAST_DUE_MESSAGE');
  }
  return t('APP_GLOBAL.ACCOUNT_SUSPENDED.MESSAGE');
});

const openBilling = () => {
  router.push({
    name: 'vincto_billing_index',
    params: { accountId: accountId.value },
  });
};

const payInvoice = () => {
  window.open(subscription.value.invoice_url, '_blank');
};

const talkToUs = () => {
  window.open(t('APP_GLOBAL.ACCOUNT_SUSPENDED.CONTACT_URL'), '_blank');
};

const toggleSupportWidgetVisibility = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggleBubbleVisibility('show');
  }
};

const setupListenerForWidgetEvent = () => {
  window.addEventListener('chatwoot:on-message', () => {
    toggleSupportWidgetVisibility();
  });
};

onMounted(() => {
  // o show da conta funciona mesmo suspensa e traz o vincto_subscription
  store.dispatch('accounts/get');
  toggleSupportWidgetVisibility();
  setupListenerForWidgetEvent();
});
</script>

<template>
  <div
    class="flex items-center justify-center h-full w-full bg-n-background p-4"
  >
    <div
      class="flex flex-col items-center max-w-lg w-full text-center bg-white dark:bg-n-solid-2 rounded-2xl border border-n-weak shadow-sm px-8 py-10"
    >
      <img
        v-if="globalConfig.logo"
        :src="globalConfig.logo"
        :alt="globalConfig.installationName"
        class="block w-auto h-8 mb-6 dark:hidden"
      />
      <img
        v-if="globalConfig.logoDark"
        :src="globalConfig.logoDark"
        :alt="globalConfig.installationName"
        class="hidden w-auto h-8 mb-6 dark:block"
      />
      <h1 class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12 mb-3">
        {{ title }}
      </h1>
      <p class="text-sm text-n-slate-11 mb-8">
        {{ message }}
      </p>
      <div class="flex flex-col gap-3 w-full max-w-xs">
        <NextButton
          v-if="isPastDue && subscription.invoice_url"
          lg
          class="w-full font-semibold"
          :label="$t('APP_GLOBAL.ACCOUNT_SUSPENDED.PAY_INVOICE')"
          @click="payInvoice"
        />
        <NextButton
          v-if="isAdmin"
          lg
          class="w-full font-semibold"
          :label="$t('APP_GLOBAL.ACCOUNT_SUSPENDED.SUBSCRIBE_NOW')"
          @click="openBilling"
        />
        <NextButton
          lg
          faded
          slate
          class="w-full font-medium"
          :label="$t('APP_GLOBAL.ACCOUNT_SUSPENDED.TALK_TO_US')"
          @click="talkToUs"
        />
      </div>
    </div>
  </div>
</template>
